import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { BasicAuthForm } from '../components/BasicAuthForm.js';
import { StatusPageCard } from '../components/StatusPageCard.js';
import { Verification } from '../components/Verification.js';
import { usePlans } from '../components/site.js';
import {
  Button,
  Card,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';
import { pageOf } from './Speed.js';

/** Uptime intervals offered; the plan sets the shortest (pricing §2). */
const INTERVALS = [60, 180, 300, 600, 900, 1800, 3600];
/** Until the plans are loaded: the same values as `minIntervalSeconds` of the plans. */
const MIN_INTERVAL: Record<string, number> = { free: 300, starter: 180, business: 60, agency: 60 };

/** A page of the site: a path ("/termine", as in the sheet) on the site's address, or a full one. */
const pageUrl = (page: string, siteUrl: string) =>
  page.startsWith('/') ? new URL(page, siteUrl).href : page;

/** Where a check looks: the path, with the host when it is another one of the domain (www …). */
const where = (url: string, siteUrl: string) => {
  const host = new URL(url).host;
  return host === new URL(siteUrl).host ? pageOf(url) : `${host}${pageOf(url)}`;
};

type Watched = 'text' | 'phone' | 'address' | 'cta';
const WATCHED: Watched[] = ['text', 'phone', 'address', 'cta'];
type KeywordConfig = {
  keyword: string;
  mode: 'present' | 'absent';
  case_sensitive: boolean;
  what?: Watched;
  selector?: string;
};

/** Name and address; the address is the one checked, not editable (audit, site-settings.html). */
function General({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [name, setName] = useState(site.name);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/sites/{siteId}', {
          params: { path: { siteId: site.id } },
          body: { name: name.trim() },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', site.id] }),
  });
  return (
    <Card>
      <h2>{t('settings.general')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <div className="fields">
          <TextField
            label={t('settings.name')}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? undefined : t('errors.required')}
          />
          <TextField
            label={t('settings.url')}
            value={site.url}
            readOnly
            hint={t('settings.urlHint')}
          />
        </div>
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        <div className="acts">
          <Button type="submit" size="sm" busy={save.isPending}>
            {t('app.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The interval of the uptime check (saved with the button) and the pause (at once). */
function Monitoring({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const planCode = useMe().data?.account.planCode ?? 'free';
  const plans = usePlans().data?.items;
  const http = site.monitors.find(
    (m) => m.type === 'http' && (m.url === null || m.url === site.url),
  );
  const refresh = () => client.invalidateQueries({ queryKey: ['site', site.id] });
  const pause = useMutation({
    mutationFn: (status: 'active' | 'paused') =>
      unwrap(
        api.PATCH('/sites/{siteId}', { params: { path: { siteId: site.id } }, body: { status } }),
      ),
    onSuccess: refresh,
  });
  const [seconds, setSeconds] = useState<number | null>(null);
  const chosen = seconds ?? http?.intervalSeconds;
  const interval = useMutation({
    mutationFn: (intervalSeconds: number) =>
      unwrap(
        api.PATCH('/monitors/{monitorId}', {
          params: { path: { monitorId: http?.id ?? '' } },
          body: { intervalSeconds },
        }),
      ),
    onSuccess: refresh,
  });
  const min =
    plans?.find((p) => p.code === planCode)?.limits.minIntervalSeconds ??
    MIN_INTERVAL[planCode] ??
    300;
  const options = INTERVALS.filter((s) => s >= min);
  if (http && !options.includes(http.intervalSeconds)) options.push(http.intervalSeconds);
  options.sort((a, b) => a - b);
  // "Free: ab 5 Minuten. Kürzere Intervalle ab Starter." – the next plan that checks more often.
  const shorter = plans?.find((p) => p.limits.minIntervalSeconds < min);
  const hint = [
    t('settings.fromMinutes', { plan: t(`admin.plans.${planCode}`), count: min / 60 }),
    shorter ? t('settings.shorterFrom', { plan: t(`admin.plans.${shorter.code}`) }) : null,
  ]
    .filter(Boolean)
    .join(' ');
  const paused = site.status === 'paused';
  return (
    <Card>
      <h2>{t('settings.monitoring')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (chosen !== undefined) interval.mutate(chosen);
        }}
      >
        <div className="fields">
          {http ? (
            <SelectField
              label={t('settings.interval')}
              hint={hint}
              value={String(chosen)}
              onChange={(e) => {
                interval.reset();
                setSeconds(Number(e.target.value));
              }}
            >
              {options.map((s) => (
                <option key={s} value={s}>
                  {s < 3600
                    ? t('settings.minutes', { count: s / 60 })
                    : t('settings.hours', { count: s / 3600 })}
                </option>
              ))}
            </SelectField>
          ) : null}
          <div className="field">
            <span className="label">{t('settings.state')}</span>
            <div className="state">
              <span className={`badge ${paused ? 'warn' : 'ok'}`}>
                {paused ? t('settings.paused') : t('settings.active')}
              </span>
              <Button
                variant="secondary"
                size="sm"
                busy={pause.isPending}
                onClick={() => pause.mutate(paused ? 'active' : 'paused')}
              >
                {paused ? t('settings.resume') : t('settings.pause')}
              </Button>
            </div>
            {paused ? <p className="hint">{t('settings.pausedText')}</p> : null}
          </div>
        </div>
        {pause.isError ? <Notice kind="error">{errorMessage(pause.error, t)}</Notice> : null}
        {interval.isError ? <Notice kind="error">{errorMessage(interval.error, t)}</Notice> : null}
        {interval.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        {http ? (
          <div className="acts">
            <Button type="submit" size="sm" busy={interval.isPending}>
              {t('app.save')}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

/** Keyword monitors (T-62): what is watched where, and a form to add one; on Free the plans. */
function KeywordMonitors({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<'present' | 'absent'>('present');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [what, setWhat] = useState<Watched>('text');
  const [selector, setSelector] = useState('');
  const [error, setError] = useState<string>();
  const refresh = () => client.invalidateQueries({ queryKey: ['site', site.id] });
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/monitors', {
          params: { path: { siteId: site.id } },
          body: {
            type: 'keyword',
            ...(url.trim() ? { url: pageUrl(url.trim(), site.url) } : {}),
            config: {
              keyword: keyword.trim(),
              // Only free text can be forbidden; a phone, address or button must be there.
              mode: what === 'text' ? mode : 'present',
              case_sensitive: what === 'phone' ? false : caseSensitive,
              what,
              ...(selector.trim() ? { selector: selector.trim() } : {}),
            },
          },
        }),
      ),
    onSuccess: async () => {
      setKeyword('');
      setUrl('');
      setSelector('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (monitorId: string) =>
      unwrap(api.DELETE('/monitors/{monitorId}', { params: { path: { monitorId } } })),
    onSuccess: refresh,
  });
  const planCode = useMe().data?.account.planCode;
  const max = usePlans().data?.items.find((p) => p.code === planCode)?.limits
    .keywordMonitorsPerSite;
  const keywords = site.monitors.filter((m) => m.type === 'keyword');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return setError(t('errors.required'));
    setError(undefined);
    add.mutate();
  };
  let failure: string | undefined;
  if (add.error instanceof ApiError && add.error.code === 'PLAN_LIMIT_REACHED')
    failure = t('settings.keywords.limit', { max: add.error.problem.limit?.max ?? 0 });
  else if (add.error) failure = errorMessage(add.error, t);

  return (
    <Card>
      <h2>
        {t('settings.keywords.title')} <small>{t('settings.keywords.subtitle')}</small>
      </h2>
      {keywords.length > 0 ? (
        <ul className="kw">
          {keywords.map((m) => {
            const c = m.config as KeywordConfig;
            return (
              <li key={m.id}>
                <div>
                  <b>{t(`settings.keywords.whats.${c.what ?? 'text'}`)}</b> „{c.keyword}“
                  <small>
                    {[
                      t('settings.keywords.on', { page: where(m.url ?? site.url, site.url) }),
                      t(`settings.keywords.modes.${c.mode}`),
                      c.selector
                        ? t('settings.keywords.inSection', { selector: c.selector })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  busy={remove.isPending && remove.variables === m.id}
                  onClick={() => remove.mutate(m.id)}
                  aria-label={t('settings.keywords.removeLabel', { keyword: c.keyword })}
                >
                  {t('settings.keywords.remove')}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {remove.isError ? <Notice kind="error">{errorMessage(remove.error, t)}</Notice> : null}
      {planCode === 'free' ? (
        <div className="upsell">
          <span>{t('settings.keywords.notInPlan')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('settings.plans')}
          </LinkButton>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <b className="h4s">{t('settings.keywords.what')}</b>
          {failure ? <Notice kind="error">{failure}</Notice> : null}
          <div className="fields">
            <SelectField
              label={t('settings.keywords.type')}
              value={what}
              onChange={(e) => setWhat(e.target.value as Watched)}
            >
              {WATCHED.map((w) => (
                <option key={w} value={w}>
                  {t(`settings.keywords.whats.${w}`)}
                </option>
              ))}
            </SelectField>
            <TextField
              label={t('settings.keywords.value')}
              hint={t(`settings.keywords.valueHints.${what}`)}
              value={keyword}
              maxLength={200}
              onChange={(e) => setKeyword(e.target.value)}
              error={error}
            />
          </div>
          <div className="fields">
            <TextField
              label={t('settings.keywords.url')}
              placeholder={t('settings.keywords.urlPlaceholder')}
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {/* Audit: the condition only for text – a phone, address or button must be there. */}
            {what === 'text' ? (
              <SelectField
                label={t('settings.keywords.mode')}
                value={mode}
                onChange={(e) => setMode(e.target.value as 'present' | 'absent')}
              >
                <option value="present">{t('settings.keywords.modes.present')}</option>
                <option value="absent">{t('settings.keywords.modes.absent')}</option>
              </SelectField>
            ) : null}
          </div>
          {what !== 'phone' ? (
            <Checkbox
              label={t('settings.keywords.caseSensitive')}
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
            />
          ) : null}
          <details>
            <summary>{t('settings.keywords.sectionSummary')}</summary>
            <div className="body">
              <TextField
                label={t('settings.keywords.section')}
                hint={t('settings.keywords.sectionHint')}
                maxLength={200}
                value={selector}
                onChange={(e) => setSelector(e.target.value)}
              />
            </div>
          </details>
          <div className="acts">
            <Button type="submit" size="sm" busy={add.isPending}>
              {t('settings.keywords.add')}
            </Button>
            {max ? (
              <span className="hint">
                {t('settings.keywords.used', { used: keywords.length, count: max })}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </Card>
  );
}

/** Removal after a confirmation in place (site-settings.html .ignore-form). */
function DeleteSite({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.DELETE('/sites/{siteId}', { params: { path: { siteId: site.id } } })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['sites'] });
      void navigate('/');
    },
  });
  return (
    <Card>
      <h2>{t('settings.delete.title')}</h2>
      <p className="bodyM">{t('settings.delete.text')}</p>
      {confirm ? (
        <div className="ignore-form">
          <b className="h4s">{t('settings.delete.confirm', { name: site.name })}</b>
          <div className="acts">
            <Button
              variant="danger"
              size="sm"
              busy={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('settings.delete.yes')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
              {t('app.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="acts">
          <Button variant="danger" size="sm" onClick={() => setConfirm(true)}>
            {t('settings.delete.button')}
          </Button>
        </div>
      )}
      {remove.isError ? <Notice kind="error">{errorMessage(remove.error, t)}</Notice> : null}
    </Card>
  );
}

/**
 * Site settings (site-settings.html): name, interval and pause, keyword monitors, site password,
 * ownership, public status page (T-70), removal – cards one below the other, the heading always.
 */
export function SiteSettings() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const site = useQuery({
    queryKey: ['site', siteId, 'detail'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}', { params: { path: { siteId } } })),
  });
  return (
    <div className="sections">
      <div>
        <h1>{t('settings.title')}</h1>
      </div>
      {site.isPending ? <Loading /> : null}
      {site.isError ? (
        <LoadError message={errorMessage(site.error, t)} onRetry={() => void site.refetch()} />
      ) : null}
      {site.data ? (
        <>
          <General key={site.data.id} site={site.data} />
          <Monitoring site={site.data} />
          <KeywordMonitors site={site.data} />
          <BasicAuthForm key={`auth-${site.data.basicAuth?.username ?? ''}`} site={site.data} />
          <Verification site={site.data} />
          <StatusPageCard siteId={site.data.id} />
          <DeleteSite site={site.data} />
        </>
      ) : null}
    </div>
  );
}
